import { nativePopupFeatures } from './placement';
import type { WindowMode } from './WindowProviderContext';

/**
 * @internal
 *
 * Native-window lifecycle, kept off React's mount lifecycle and reachable from
 * outside React (the store), for two reasons:
 *
 * 1. Popup-blocker safety. A browser only allows `window.open` while a user
 *    gesture is on the call stack. The store's `openInWindow` thunk runs its
 *    synchronous body in the dispatching click, so it calls {@link reserve}
 *    there to open the popup in-gesture; `NativeWindow` then adopts it. Opening
 *    later from an effect would be past the gesture and blocked.
 *
 *    The popup opens at {@link WINDOW_HOST_PATH} — a real same-origin blank
 *    page — rather than `about:blank`. `about:blank` has no document URL, so
 *    embedded cross-origin iframes (YouTube videos in rich-text content) are
 *    sent with an empty HTTP Referer and refuse to load; a real URL makes the
 *    browser send the page's origin as Referer. The trade-off is that the host
 *    document loads asynchronously, so the renderer waits for it before
 *    portalling content in (unlike `about:blank`, whose body is immediate).
 *
 * 2. StrictMode resilience. React (dev) double-invokes effects: setup → cleanup
 *    → setup. {@link register} records the live window and cleanup
 *    {@link scheduleRelease}s a *deferred* teardown; an immediate remount
 *    {@link reclaim}s it and cancels the teardown. The synchronous
 *    setup → cleanup → setup always wins the zero-delay race.
 *
 * A single module-level instance is intentional: it is the bridge between the
 * store (which triggers the in-gesture open) and the renderer (which fills the
 * popup). It is never exposed to feature consumers — they only dispatch the
 * store's `openInWindow`/`moveToWindow`.
 */

/** A fully set-up live native window, owned across React (re)mounts. */
export interface LiveNativeWindow {
    /** The popup window. */
    popup: Window;
    /** Container element the renderer portals content into. */
    container: HTMLElement;
    /** Host the pinned title bar portals into, or `null` when none. */
    titleBarHost: HTMLElement | null;
    /** Tears the window down: removes listeners, relocates content out, closes. */
    dispose: () => void;
}

/**
 * Same-origin blank page native popups open at, instead of `about:blank`, so
 * their document has a real URL (see the module doc — keeps embedded iframes'
 * Referer valid). Served as a static file from `public/`. The renderer waits
 * for the popup to finish loading this path before adopting the document.
 */
export const WINDOW_HOST_PATH = '/window-host.html';

// Default to `virtual` so `reserve` is inert until a native `SmlWindowProvider`
// announces itself: with no native renderer mounted, opening a popup would only
// orphan it.
let mode: WindowMode = 'virtual';
const reservations = new Map<string, Window>();
const liveWindows = new Map<string, LiveNativeWindow>();
const releaseTimers = new Map<string, ReturnType<typeof setTimeout>>();

const openWindowCount = () =>
    [...liveWindows.values()].filter((w) => !w.popup.closed).length;

export const nativeWindowManager = {
    /** Sets the active windowing primitive; `reserve` is a no-op unless native. */
    setMode(next: WindowMode): void {
        mode = next;
    },

    /**
     * Opens the {@link WINDOW_HOST_PATH} popup now and holds it for `id`. Call
     * synchronously inside a user-gesture handler (a store thunk dispatched from
     * a click). No-op in virtual mode, or when `id` is already reserved or live.
     */
    reserve(id: string): void {
        if (mode !== 'native') {
            return;
        }
        const liveEntry = liveWindows.get(id);
        if (liveEntry && !liveEntry.popup.closed) {
            return;
        }
        const reserved = reservations.get(id);
        if (reserved && !reserved.closed) {
            return;
        }
        const popup = window.open(
            WINDOW_HOST_PATH,
            `sml-window-${id}`,
            nativePopupFeatures(openWindowCount()),
        );
        if (popup) {
            reservations.set(id, popup);
        }
    },

    /**
     * Returns the reserved-but-not-yet-registered popup for `id`, or `null`,
     * without clearing the reservation — {@link register} clears it once the
     * renderer has adopted the popup. Holding it until then lets a StrictMode
     * remount (setup → cleanup → setup, which runs before the host page has
     * loaded) re-adopt the same popup instead of re-navigating it.
     */
    peekReservation(id: string): Window | null {
        const popup = reservations.get(id) ?? null;
        return popup && !popup.closed ? popup : null;
    },

    /**
     * Reclaims the still-open live window for `id`, cancelling its pending
     * deferred teardown, so a (re)mount reuses it instead of reopening. Returns
     * `null` when none is live.
     */
    reclaim(id: string): LiveNativeWindow | null {
        const timer = releaseTimers.get(id);
        if (timer !== undefined) {
            clearTimeout(timer);
            releaseTimers.delete(id);
        }
        const live = liveWindows.get(id);
        if (live && !live.popup.closed) {
            return live;
        }
        if (live) {
            liveWindows.delete(id);
        }
        return null;
    },

    /** Records a freshly opened + set-up window for `id`, clearing its reservation. */
    register(id: string, live: LiveNativeWindow): void {
        liveWindows.set(id, live);
        reservations.delete(id);
    },

    /**
     * Schedules a deferred teardown of `id`'s window; a {@link reclaim} before it
     * fires cancels it (so a StrictMode remount keeps the window).
     */
    scheduleRelease(id: string): void {
        if (releaseTimers.has(id)) {
            return;
        }
        const timer = setTimeout(() => {
            releaseTimers.delete(id);
            const live = liveWindows.get(id);
            if (live) {
                liveWindows.delete(id);
                live.dispose();
            }
        }, 0);
        releaseTimers.set(id, timer);
    },
};
